import React from "react";
import { useParams } from "react-router-dom";
import { base44 } from "@/api/base44Client";
import { Copy, CheckCircle2, Trophy, Users, Gift, Share2, Mail, MessageCircle, Send, Award } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import PageTransition from "@/components/mobile/PageTransition";
import HeroLayout from "@/components/hero/HeroLayout";
import { toast } from "@/components/ui/use-toast";
import PublicBreadcrumbs from "@/components/public/PublicBreadcrumbs";
import { useLanguage } from "@/lib/LanguageContext";
import { getAffiliateTranslations } from "@/i18n";
import { getLocalizedPath } from "@/lib/i18nRoutes";
import { useTranslation } from "@/i18n";

const DATE_LOCALES = {
  en: "en-US",
  he: "he-IL",
  es: "es-ES",
  it: "it-IT",
  fr: "fr-FR",
  de: "de-DE",
  pt: "pt-PT",
  ar: "ar",
  el: "el-GR",
  ru: "ru-RU",
  tr: "tr-TR",
  zh: "zh-CN",
};

function makeReferralCode(user) {
  const seed = (user?.id || user?.email || Math.random().toString(36)).replace(/[^a-z0-9]/gi, "").slice(-5).toUpperCase();
  return `MAR-${seed}${Math.random().toString(36).slice(2, 4).toUpperCase()}`;
}

function daysLeft(date) {
  if (!date) return 0;
  return Math.max(0, Math.ceil((new Date(date) - new Date()) / 86400000));
}

function formatReferralDate(date, language) {
  if (!date) return "—";
  return new Date(date).toLocaleString(DATE_LOCALES[language] || undefined, { dateStyle: "medium", timeStyle: "short" });
}

export default function Affiliate() {
  const { lang: routeLang } = useParams();
  const { language } = useLanguage();
  const { t } = useTranslation();
  const currentLanguage = routeLang || language || "en";
  const tx = getAffiliateTranslations(currentLanguage);
  const publicCopy = t("public", { defaultValue: {} });
  const isRTL = currentLanguage === "he" || currentLanguage === "ar";
  const [user, setUser] = React.useState(null);
  const [profile, setProfile] = React.useState(null);
  const [registrations, setRegistrations] = React.useState([]);
  const [milestones, setMilestones] = React.useState([]);
  const [rewards, setRewards] = React.useState([]);
  const [notifications, setNotifications] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [copied, setCopied] = React.useState("");

  const affiliatePath = getLocalizedPath("affiliate", currentLanguage);
  const homePath = getLocalizedPath("home", currentLanguage);
  const referralLink = profile ? `https://marstick.com${affiliatePath}?ref=${profile.referral_code}` : "";
  const approved = registrations.filter((item) => item.status === "approved").length;
  const pending = registrations.filter((item) => item.status === "pending").length;
  const rejected = registrations.filter((item) => ["rejected", "fraudulent"].includes(item.status)).length;
  const nextMilestone = milestones.find((m) => (m.threshold || 0) > approved);
  const previousThreshold = milestones.filter((m) => (m.threshold || 0) <= approved).pop()?.threshold || 0;
  const progressTarget = nextMilestone?.threshold || Math.max(approved, 1);
  const progress = Math.min(100, Math.round(((approved - previousThreshold) / Math.max(progressTarget - previousThreshold, 1)) * 100));

  const load = React.useCallback(async () => {
    const isAuthed = await base44.auth.isAuthenticated();
    if (!isAuthed) {
      setLoading(false);
      return;
    }
    const currentUser = await base44.auth.me();
    setUser(currentUser);
    let profiles = await base44.entities.AffiliateProfile.filter({ user_email: currentUser.email }, "-updated_date", 1);
    let currentProfile = profiles[0];
    if (!currentProfile) {
      currentProfile = await base44.entities.AffiliateProfile.create({
        user_email: currentUser.email,
        user_name: currentUser.full_name || currentUser.email,
        referral_code: makeReferralCode(currentUser),
        status: "active",
        last_activity_at: new Date().toISOString(),
      });
    }
    const [refs, ms, rw, notes] = await Promise.all([
      base44.entities.ReferralRegistration.filter({ referrer_email: currentUser.email }, "-created_date", 100),
      base44.entities.AffiliateMilestone.filter({ is_active: true }, "threshold", 20),
      base44.entities.AffiliateReward.filter({ user_email: currentUser.email }, "-created_date", 20),
      base44.entities.AffiliateNotification.filter({ user_email: currentUser.email }, "-created_at", 10),
    ]);
    setProfile(currentProfile);
    setRegistrations(refs);
    setMilestones(ms);
    setRewards(rw);
    setNotifications(notes);
    setLoading(false);
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const copyToClipboard = async (text, key) => {
    await navigator.clipboard.writeText(text);
    setCopied(key);
    toast({ title: tx.copied });
    setTimeout(() => setCopied(""), 1800);
  };

  const shareUrl = encodeURIComponent(referralLink);
  const shareText = encodeURIComponent(tx.shareText);
  const shareLinks = [
    { label: "WhatsApp", icon: MessageCircle, href: `https://wa.me/?text=${shareText}%20${shareUrl}` },
    { label: "Telegram", icon: Send, href: `https://t.me/share/url?url=${shareUrl}&text=${shareText}` },
    { label: "Facebook", icon: Share2, href: `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}` },
    { label: "LinkedIn", icon: Share2, href: `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}` },
    { label: "Reddit", icon: Share2, href: `https://www.reddit.com/submit?url=${shareUrl}&title=${shareText}` },
    { label: "Email", icon: Mail, href: `mailto:?subject=${encodeURIComponent(tx.emailSubject)}&body=${shareText}%0A${shareUrl}` },
  ];

  if (loading) return <PageTransition><div className="p-6">{tx.loading}</div></PageTransition>;

  if (!user) {
    return <PageTransition><div dir={isRTL ? "rtl" : "ltr"} className="maritime-page-shell"><PublicBreadcrumbs items={[{ label: publicCopy.common.home, href: homePath }, { label: tx.heroTitle }]} /><HeroLayout title={tx.heroTitle} subtitle={tx.loginSubtitle} /><Button onClick={() => base44.auth.redirectToLogin(window.location.href)}>{tx.loginButton}</Button></div></PageTransition>;
  }

  return (
    <PageTransition>
      <div dir={isRTL ? "rtl" : "ltr"} className="maritime-page-shell">
        <PublicBreadcrumbs items={[{ label: publicCopy.common.home, href: homePath }, { label: tx.heroTitle }]} />
        <HeroLayout
          eyebrow={<Badge className="border-white/25 bg-white/15 text-white">{tx.rewardsBadge}</Badge>}
          title={tx.heroTitle}
          subtitle={tx.heroSubtitle}
          className="!min-h-[18rem] md:!min-h-[22rem] lg:!min-h-[24rem] [&_.hero-inner]:!py-10 [&_.hero-title]:!text-[clamp(2.25rem,4.5vw,4rem)]"
        />

        <div className="grid gap-4 md:grid-cols-4">
          {[{ label: tx.approved, value: approved, icon: CheckCircle2 }, { label: tx.pending, value: pending, icon: Users }, { label: tx.rejected, value: rejected, icon: Award }, { label: tx.premiumDays, value: profile?.lifetime_premium ? "∞" : daysLeft(profile?.premium_unlocked_until), icon: Gift }].map((stat) => {
            const Icon = stat.icon;
            return <Card key={stat.label}><CardContent className="flex items-center gap-3 p-5"><Icon className="h-8 w-8 text-primary" /><div><p className="text-2xl font-black tabular-nums">{stat.value}</p><p className="text-sm text-muted-foreground">{stat.label}</p></div></CardContent></Card>;
          })}
        </div>

        <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Share2 className="h-5 w-5 text-primary" /> {tx.toolkitTitle}</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-[1fr_auto]">
                <div className="space-y-3">
                  <label className="text-sm font-bold" htmlFor="ref-code">{tx.referralCode}</label>
                  <div className="flex gap-2"><input id="ref-code" readOnly value={profile.referral_code} className="flex-1 rounded-xl border border-border bg-background px-4 py-3 font-mono font-bold" /><Button variant="outline" onClick={() => copyToClipboard(profile.referral_code, "code")} aria-label={tx.copyReferralCode}>{copied === "code" ? <CheckCircle2 /> : <Copy />}</Button></div>
                  <label className="text-sm font-bold" htmlFor="ref-link">{tx.referralLink}</label>
                  <div className="flex gap-2"><input id="ref-link" readOnly value={referralLink} className="flex-1 rounded-xl border border-border bg-background px-4 py-3 text-sm" /><Button variant="outline" onClick={() => copyToClipboard(referralLink, "link")} aria-label={tx.copyReferralLink}>{copied === "link" ? <CheckCircle2 /> : <Copy />}</Button></div>
                  <p className="text-xs leading-5 text-muted-foreground">{tx.referralNote}</p>
                </div>
                <div className="rounded-2xl border border-border bg-muted/30 p-5 text-center">
                  <img width="220" height="220" alt={tx.qrAlt} className="mx-auto rounded-xl bg-white p-2" referrerPolicy="no-referrer" src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=8&data=${encodeURIComponent(referralLink)}`} />
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {shareLinks.map((item) => { const Icon = item.icon; return <Button key={item.label} variant="outline" asChild><a href={item.href} target="_blank" rel="noopener noreferrer"><Icon className="h-4 w-4" /> {item.label}</a></Button>; })}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Trophy className="h-5 w-5 text-primary" /> {tx.nextUnlock}</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <Badge variant="outline" className="text-sm">{profile.current_badge || tx.newAmbassador}</Badge>
              {nextMilestone ? <><div><p className="text-2xl font-black">{nextMilestone.label}</p><p className="text-sm text-muted-foreground">{Math.max(nextMilestone.threshold - approved, 0)} {tx.moreApproved} {nextMilestone.is_lifetime ? tx.lifetimePremium : `${nextMilestone.premium_days} ${tx.daysPremium}`}.</p></div><div className="h-3 overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-primary" style={{ width: `${progress}%` }} /></div><p className="text-xs font-bold text-muted-foreground">{approved}/{nextMilestone.threshold} {tx.approvedProgress}</p></> : <p className="text-sm text-muted-foreground">{tx.allMilestones}</p>}
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader><CardTitle>{tx.milestones}</CardTitle></CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-5">
            {milestones.map((m) => <div key={m.id} className={`rounded-2xl border p-4 ${approved >= m.threshold ? "border-primary bg-primary/10" : "border-border bg-card"}`}><p className="font-black">{m.threshold} {tx.referrals}</p><p className="text-sm text-muted-foreground">{m.is_lifetime ? tx.lifetimePremium : `${m.premium_days} ${tx.daysPremium}`}</p><Badge variant="outline" className="mt-3">{m.badge}</Badge></div>)}
          </CardContent>
        </Card>

        <div className="grid gap-6 lg:grid-cols-[1fr_0.8fr]">
        <Card>
          <CardHeader><CardTitle>{tx.referralReport}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-primary/30 bg-primary/10 p-4"><p className="text-2xl font-black">{approved}</p><p className="text-sm text-muted-foreground">{tx.approvedReferrals}</p></div>
              <div className="rounded-2xl border border-border bg-card p-4"><p className="text-2xl font-black">{rejected}</p><p className="text-sm text-muted-foreground">{tx.rejectedReferrals}</p></div>
            </div>
            {registrations.length === 0 ? <p className="text-sm text-muted-foreground">{tx.noReferrals}</p> : <div className="overflow-x-auto rounded-2xl border border-border"><table data-keep-table className="min-w-full text-sm"><thead className="bg-muted/50"><tr><th className="px-4 py-3 text-left font-bold">{tx.userId}</th><th className="px-4 py-3 text-left font-bold">{tx.timestamp}</th><th className="px-4 py-3 text-left font-bold">{tx.status}</th></tr></thead><tbody>{registrations.map((item) => <tr key={item.id} className="border-t border-border"><td className="px-4 py-3 font-mono text-xs">{item.referred_user_id || tx.notAssigned}</td><td className="px-4 py-3 text-muted-foreground">{formatReferralDate(item.registered_at || item.created_date, currentLanguage)}</td><td className="px-4 py-3"><Badge variant={item.status === "approved" ? "default" : "outline"}>{tx.statusLabels[item.status] || item.status}</Badge></td></tr>)}</tbody></table></div>}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>{tx.notifications}</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {notifications.length === 0 ? <p className="text-sm text-muted-foreground">{tx.noNotifications}</p> : notifications.map((item) => <div key={item.id} className="rounded-2xl border border-border p-4"><p className="font-bold">{item.title}</p><p className="text-sm text-muted-foreground">{item.message}</p></div>)}
          </CardContent>
        </Card>
        </div>
      </div>
    </PageTransition>
  );
}